// frontend/admin-portal/src/components/products/CategoryStatsCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Checkbox,
  Badge,
  Spinner,
  Title,
  SearchSelect,
  formatMoney,
} from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";
import { useSettings } from "../../contexts/SettingsContext";
import { resolveImageUrl } from "../../services/fileService";

const KIND_LABELS = {
  passive: "Passive",
  active: "Active",
  cable: "Cable",
  instrument: "Instrument",
  accessory: "Accessory",
  device: "Device",
  hardware: "Hardware",
  sfp: "SFP / Transceivers",
  splitter: "Splitters",
  odf: "ODF / Patch Panels",
  spliceProtector: "Splice Protectors",
  cleaner: "Cleaners",
  puller: "Pullers",
  tool: "Tools",
  toolKit: "Tool Kits",
};

const KIND_ORDER = [
  "passive",
  "active",
  "cable",
  "sfp",
  "splitter",
  "odf",
  "instrument",
  "tool",
  "toolKit",
  "accessory",
  "spliceProtector",
  "cleaner",
  "puller",
  "hardware",
  "device",
];

const MAX_PER_CATEGORY = 24;

export default function CategoryStatsCard({ refreshKey = 0 }) {
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "IQD");

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ items: [], total: 0, totalValue: 0 });
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  // Products grouped by categoryId, loaded lazily.
  const [productsByCat, setProductsByCat] = useState({});

  // Product IDs hidden by the user from the panel (not deleted).
  const [hiddenProductIds, setHiddenProductIds] = useState(() => new Set());

  // ── Load category aggregates ────────────────────────────────
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    productService
      .categoryStats({ isActive: true })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.response?.data?.message || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const sortedItems = useMemo(
    () =>
      [...data.items].sort(
        (a, b) => b.count - a.count || a.name.localeCompare(b.name),
      ),
    [data.items],
  );

  const selectOptions = useMemo(() => {
    if (!sortedItems.length) return [];
    const grouped = sortedItems.reduce((acc, c) => {
      const kind = c.kind || "other";
      if (!acc[kind]) acc[kind] = [];
      acc[kind].push(c);
      return acc;
    }, {});
    return Object.entries(grouped)
      .sort(([a], [b]) => {
        const ai = KIND_ORDER.indexOf(a);
        const bi = KIND_ORDER.indexOf(b);
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      })
      .map(([kind, list]) => ({
        group: KIND_LABELS[kind] || kind,
        options: list.map((c) => ({
          value: String(c.categoryId),
          label: `${c.name} (${c.count})`,
        })),
      }));
  }, [sortedItems]);

  const filteredItems = useMemo(() => {
    let list = sortedItems;
    if (search)
      list = list.filter((c) => String(c.categoryId) === String(search));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q) ||
          c.kind.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedItems, search, query]);

  const selectedItems = useMemo(
    () => sortedItems.filter((i) => selected.has(i.categoryId)),
    [sortedItems, selected],
  );

  const summary = useMemo(() => {
    const base = selectedItems.length > 0 ? selectedItems : sortedItems;
    return {
      count: base.reduce((s, i) => s + i.count, 0),
      value: base.reduce((s, i) => s + i.totalValue, 0),
      stock: base.reduce((s, i) => s + i.totalStock, 0),
      categories: base.length,
    };
  }, [selectedItems, sortedItems]);

  // ── Load products for newly selected categories ─────────────
  useEffect(() => {
    if (!open || selected.size === 0) return;

    const needed = Array.from(selected).filter(
      (id) => productsByCat[id] === undefined,
    );
    if (needed.length === 0) return;

    let alive = true;

    setProductsByCat((prev) => {
      const next = { ...prev };
      needed.forEach((id) => {
        next[id] = { loading: true, items: [], total: 0 };
      });
      return next;
    });

    productService
      .list({
        categoryIds: needed.join(","),
        limit: 500,
        page: 1,
        isActive: true,
        sortBy: "name",
        sortDir: "asc",
      })
      .then(({ items, pagination }) => {
        if (!alive) return;

        const buckets = {};
        needed.forEach((id) => {
          buckets[Number(id)] = [];
        });
        for (const p of items) {
          const key = Number(p.categoryId);
          if (buckets[key]) buckets[key].push(p);
        }

        setProductsByCat((prev) => {
          const next = { ...prev };
          for (const id of needed) {
            const bucket = buckets[Number(id)] || [];
            next[id] = {
              loading: false,
              items: bucket.slice(0, MAX_PER_CATEGORY),
              total: bucket.length,
            };
          }
          return next;
        });
      })
      .catch((err) => {
        if (!alive) return;
        setProductsByCat((prev) => {
          const next = { ...prev };
          needed.forEach((id) => {
            next[id] = {
              loading: false,
              items: [],
              total: 0,
              error: err.response?.data?.message || err.message,
            };
          });
          return next;
        });
      });

    return () => {
      alive = false;
    };
  }, [selected, open]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const visibleIds = filteredItems.map((c) => c.categoryId);
  const visibleSelected = visibleIds.filter((id) => selected.has(id));
  const allVisibleChecked =
    visibleIds.length > 0 && visibleSelected.length === visibleIds.length;
  const someVisibleChecked =
    visibleSelected.length > 0 && visibleSelected.length < visibleIds.length;

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const retryCategory = (id) => {
    setProductsByCat((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // ── Product hide/show (per-card × button) ──────────────────
  const hideProduct = (productId) => {
    setHiddenProductIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const unhideProduct = (productId) => {
    setHiddenProductIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
  };

  const resetHiddenForCategory = (categoryId) => {
    const bucket = productsByCat[categoryId];
    if (!bucket) return;
    setHiddenProductIds((prev) => {
      const next = new Set(prev);
      for (const p of bucket.items) next.delete(p.id);
      return next;
    });
  };

  const isHighlighted = (id) => search && String(id) === String(search);

  return (
    <Card className="mb-3 fh-catstats">
      <button
        type="button"
        className="fh-catstats__head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="fh-catstats__head-left">
          <span
            className={`fh-catstats__chevron ${open ? "is-open" : ""}`}
            aria-hidden="true"
          >
            ▸
          </span>
          <Title level={4} className="mb-0">
            Products by category
          </Title>
          {!loading && (
            <Badge variant="default">
              {filteredItems.length === sortedItems.length
                ? `${sortedItems.length} categories`
                : `${filteredItems.length} / ${sortedItems.length} categories`}{" "}
              · {data.total} products
            </Badge>
          )}
        </div>

        <div className="fh-catstats__head-right">
          {!loading && (
            <>
              <span className="fh-catstats__pill">
                <span className="text-muted">Count</span>
                <strong>{summary.count}</strong>
              </span>
              <span className="fh-catstats__pill">
                <span className="text-muted">Stock</span>
                <strong>{summary.stock}</strong>
              </span>
              <span className="fh-catstats__pill">
                <span className="text-muted">Value</span>
                <strong>
                  {formatMoney(summary.value, { symbol: currency })}
                </strong>
              </span>
              {selected.size > 0 && (
                <Badge variant="primary">{selected.size} selected</Badge>
              )}
            </>
          )}
        </div>
      </button>

      {open && (
        <div className="fh-catstats__body">
          {error && <div className="fh-alert fh-alert--danger">{error}</div>}

          {loading ? (
            <div className="py-4 text-center">
              <Spinner size="md" />
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="text-muted py-3">No categories yet.</div>
          ) : (
            <>
              <div className="fh-catstats__filterbar">
                <div className="fh-catstats__searchselect">
                  <SearchSelect
                    value={search || null}
                    onChange={(v) => {
                      const next =
                        v && typeof v === "object" && "value" in v
                          ? v.value
                          : v;
                      setSearch(next ?? "");
                    }}
                    options={selectOptions}
                    placeholder="Jump to category…"
                    searchPlaceholder="Search categories…"
                    emptyMessage="No categories match"
                    clearable
                  />
                </div>

                <div className="fh-catstats__query">
                  <input
                    type="search"
                    className="fh-input fh-input--compact"
                    placeholder="Filter by name, slug or kind…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>

                {(search || query) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSearch("");
                      setQuery("");
                    }}
                  >
                    Reset filters
                  </Button>
                )}
              </div>

              <div className="fh-catstats__toolbar">
                <label className="fh-catstats__selectall">
                  <Checkbox
                    checked={allVisibleChecked}
                    indeterminate={someVisibleChecked}
                    onChange={toggleAllVisible}
                    ariaLabel="Select all visible categories"
                  />
                  <span>
                    Select {search || query ? "visible" : "all"} (
                    {visibleIds.length})
                  </span>
                </label>
                {selected.size > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearSelection}>
                    Clear selection ({selected.size})
                  </Button>
                )}
              </div>

              {filteredItems.length === 0 ? (
                <div className="text-muted py-3">
                  No categories match your filters.
                </div>
              ) : (
                <div className="fh-catstats__grid">
                  {filteredItems.map((cat) => {
                    const isSel = selected.has(cat.categoryId);
                    return (
                      <label
                        key={cat.categoryId}
                        className={
                          "fh-catstats__row" +
                          (isSel ? " is-selected" : "") +
                          (isHighlighted(cat.categoryId)
                            ? " is-highlighted"
                            : "")
                        }
                      >
                        <Checkbox
                          checked={isSel}
                          onChange={() => toggle(cat.categoryId)}
                          ariaLabel={`Select ${cat.name}`}
                        />
                        <div className="fh-catstats__row-main">
                          <div className="fh-catstats__row-title">
                            <span>{cat.name}</span>
                            <Badge
                              variant={cat.count > 0 ? "success" : "default"}
                            >
                              {cat.count}
                            </Badge>
                          </div>
                          <div className="fh-catstats__row-meta">
                            <span className="text-muted">{cat.kind}</span>
                            <span className="text-muted">
                              stock {cat.totalStock}
                            </span>
                            <span className="text-muted">
                              {formatMoney(cat.totalValue, {
                                symbol: currency,
                              })}
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedItems.length > 0 && (
                <div className="fh-catstats__selected">
                  <div className="fh-catstats__selected-header">
                    <Title level={5} className="mb-0">
                      Products in selected categories
                    </Title>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      {selectedItems.length} categories · {summary.count}{" "}
                      products
                    </span>
                  </div>

                  {selectedItems.map((cat) => {
                    const bucket = productsByCat[cat.categoryId];
                    const visibleProducts =
                      bucket?.items?.filter(
                        (p) => !hiddenProductIds.has(p.id),
                      ) || [];
                    const hiddenCount =
                      (bucket?.items?.length || 0) - visibleProducts.length;

                    return (
                      <div
                        key={cat.categoryId}
                        className="fh-catstats__catpanel"
                      >
                        <div className="fh-catstats__catpanel-head">
                          <div>
                            <strong>{cat.name}</strong>
                            <span
                              className="text-muted"
                              style={{ marginLeft: 8, fontSize: 12 }}
                            >
                              {cat.kind}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge
                              variant={cat.count > 0 ? "success" : "default"}
                            >
                              {cat.count} products
                            </Badge>
                            {hiddenCount > 0 && (
                              <Badge variant="warning">
                                {hiddenCount} hidden
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggle(cat.categoryId)}
                            >
                              Remove ×
                            </Button>
                          </div>
                        </div>

                        {!bucket || bucket.loading ? (
                          <div className="py-3 text-center">
                            <Spinner size="sm" />
                          </div>
                        ) : bucket.error ? (
                          <div className="fh-alert fh-alert--danger d-flex align-items-center justify-content-between">
                            <span>{bucket.error}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retryCategory(cat.categoryId)}
                            >
                              Retry
                            </Button>
                          </div>
                        ) : visibleProducts.length === 0 && hiddenCount > 0 ? (
                          <div
                            className="text-muted py-2"
                            style={{ fontSize: 13 }}
                          >
                            All {hiddenCount} products hidden.{" "}
                            <button
                              className="fh-link"
                              onClick={() =>
                                resetHiddenForCategory(cat.categoryId)
                              }
                            >
                              Restore all
                            </button>
                          </div>
                        ) : bucket.items.length === 0 ? (
                          <div
                            className="text-muted py-2"
                            style={{ fontSize: 13 }}
                          >
                            No products in this category yet.
                          </div>
                        ) : (
                          <>
                            <div className="fh-catstats__cards">
                              {visibleProducts.map((p) => (
                                <ProductMiniCard
                                  key={p.id}
                                  product={p}
                                  currency={currency}
                                  onClick={() => navigate(`/products/${p.id}`)}
                                  onRemove={() => hideProduct(p.id)}
                                />
                              ))}
                            </div>
                            <div className="fh-catstats__more d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <span>
                                Showing {visibleProducts.length} of{" "}
                                {bucket.total}
                                {hiddenCount > 0 && ` · ${hiddenCount} hidden`}
                                {bucket.total > bucket.items.length &&
                                  ` · only first ${bucket.items.length} loaded`}
                              </span>
                              {hiddenCount > 0 && (
                                <button
                                  className="fh-link"
                                  onClick={() =>
                                    resetHiddenForCategory(cat.categoryId)
                                  }
                                >
                                  Show all hidden
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/* ─── Mini product card, with a removable × ───────────────── */
function ProductMiniCard({ product, currency, onClick, onRemove }) {
  let imgs = product.images;
  if (typeof imgs === "string") {
    try {
      imgs = JSON.parse(imgs);
    } catch {
      imgs = [];
    }
  }
  if (!Array.isArray(imgs)) imgs = [];
  const first = imgs[0];
  const raw =
    first == null
      ? null
      : typeof first === "object"
        ? first.url || first.thumbnailUrl
        : first;
  const imgUrl = raw ? resolveImageUrl(raw) : null;

  const outOfStock = product.stock <= 0;
  const lowStock = product.stock > 0 && product.stock <= product.minStock;
  const unit = product.unit || "pcs";

  return (
    <div className="fh-mini-card" title={product.name}>
      <button
        type="button"
        className="fh-mini-card__remove"
        aria-label="Remove from view"
        title="Remove from view"
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.();
        }}
      >
        ×
      </button>

      <button type="button" className="fh-mini-card__main" onClick={onClick}>
        <div className="fh-mini-card__img">
          {imgUrl ? (
            <img src={imgUrl} alt={product.name} loading="lazy" />
          ) : (
            <div className="fh-mini-card__img-placeholder">
              <span>no image</span>
            </div>
          )}
          {outOfStock && (
            <span className="fh-mini-card__badge fh-mini-card__badge--danger">
              Out
            </span>
          )}
          {lowStock && (
            <span className="fh-mini-card__badge fh-mini-card__badge--warning">
              Low
            </span>
          )}
        </div>

        <div className="fh-mini-card__body">
          <div className="fh-mini-card__name">{product.name}</div>
          <div className="fh-mini-card__sku">{product.sku}</div>
          <div className="fh-mini-card__footer">
            <span className="fh-mini-card__price">
              {formatMoney(product.price, { symbol: currency })}
            </span>
            <span
              className={
                "fh-mini-card__stock" +
                (outOfStock ? " is-out" : lowStock ? " is-low" : "")
              }
            >
              {product.stock}
              <span className="fh-mini-card__unit"> {unit}</span>
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}
